import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { persistLanguagePreference } from "../i18n";

const Header = () => {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const areaBadge =
    pathname.startsWith("/it")
      ? { label: t("header.badgeIt"), className: "bg-blue-600 text-white" }
      : pathname.startsWith("/vandro-automation") || pathname.startsWith("/vandro-rental")
        ? { label: t("header.badgeAutomation"), className: "bg-[#E36330] text-white" }
      : null;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const navLinkClassIt = ({ isActive }: { isActive: boolean }) =>
    `rounded-md px-1 py-0.5 text-sm font-medium transition-colors ${
      isActive
        ? "text-blue-600 font-semibold"
        : "text-primary-700 hover:text-blue-600"
    }`;

  const navLinkClassAutomation = ({ isActive }: { isActive: boolean }) =>
    `rounded-md px-1 py-0.5 text-sm font-medium transition-colors ${
      isActive
        ? "font-semibold text-[#E36330]"
        : "text-primary-700 hover:text-[#E36330]"
    }`;

  const toggleLanguageMenu = () => {
    setIsLanguageMenuOpen(!isLanguageMenuOpen);
  };

  const changeLanguage = (lng: string) => {
    persistLanguagePreference(lng);
    i18n.changeLanguage(lng);
    setIsLanguageMenuOpen(false);
  };

  return (
    <header id="header" className={`fixed w-full z-50 transition-all duration-500 ${isScrolled ? 'bg-white/95 shadow-md' : 'bg-transparent'}`}>
      <div className="bg-white/90 backdrop-blur-md shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link
              to="/"
              className="flex items-center gap-2 rounded-lg outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-[#E36330]/40"
              onClick={() => {
                if (window.location.pathname === "/") {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
              }}
            >
              <img
                src="/logo_1.jpg"
                alt="MJ Corporation"
                className="h-10 w-10 rounded-lg border border-zinc-200/80 object-contain"
              />
              <span className="flex items-baseline gap-1 font-pretendard text-sm font-semibold">
                <span className="text-[#E36330]">MJ Corporation</span>
                {areaBadge && (
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${areaBadge.className}`}
                    aria-current="page"
                  >
                    {areaBadge.label}
                  </span>
                )}
              </span>
            </Link>
            
            {/* Desktop Navigation */}
            <nav className="hidden items-center gap-6 md:flex lg:gap-8">
              <NavLink to="/it" className={navLinkClassIt}>
                {t("header.navItSolutions")}
              </NavLink>
              <a
                href="https://vandro.ai/"
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md px-1 py-0.5 text-sm font-medium text-primary-700 transition-colors hover:text-accent-500"
              >
                {t("header.navVandro")}
              </a>
              <NavLink to="/vandro-automation" className={navLinkClassAutomation}>
                {t("header.navAutomation")}
              </NavLink>
              {/* Language Selector */}
              <div className="relative">
                <button
                  onClick={toggleLanguageMenu}
                  className="flex items-center space-x-1 text-primary-700 hover:text-accent-500 font-medium"
                >
                  <span className="text-xl">
                    {i18n.language === 'ko' ? '🇰🇷' : 
                     i18n.language === 'en' ? '🇺🇸' : 
                     i18n.language === 'ja' ? '🇯🇵' : 
                     i18n.language === 'es' ? '🇪🇸' :
                     i18n.language === 'pt' ? '🇧🇷' : '🇫🇷'}
                  </span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isLanguageMenuOpen && (
                  <div className="absolute right-0 mt-2 w-36 bg-white rounded-md shadow-lg py-1 z-50">
                    <button
                      onClick={() => changeLanguage('ko')}
                      className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <span className="text-xl mr-2">🇰🇷</span>
                      {t("header.language.ko")}
                    </button>
                    <button
                      onClick={() => changeLanguage('en')}
                      className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <span className="text-xl mr-2">🇺🇸</span>
                      {t("header.language.en")}
                    </button>
                    <button
                      onClick={() => changeLanguage('ja')}
                      className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <span className="text-xl mr-2">🇯🇵</span>
                      {t("header.language.ja")}
                    </button>
                    <button
                      onClick={() => changeLanguage('es')}
                      className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <span className="text-xl mr-2">🇪🇸</span>
                      {t("header.language.es")}
                    </button>
                    <button
                      onClick={() => changeLanguage('pt')}
                      className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <span className="text-xl mr-2">🇧🇷</span>
                      {t("header.language.pt")}
                    </button>
                    <button
                      onClick={() => changeLanguage('fr')}
                      className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <span className="text-xl mr-2">🇫🇷</span>
                      {t("header.language.fr")}
                    </button>
                  </div>
                )}
              </div>
            </nav>
            
            {/* Mobile menu button */}
            <button 
              onClick={toggleMobileMenu}
              className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 focus:outline-none"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
              </svg>
            </button>
          </div>
        </div>
        
        {/* Mobile Navigation */}
        <div className={`md:hidden bg-white border-t ${isMobileMenuOpen ? '' : 'hidden'}`}>
          <div className="container mx-auto space-y-1 px-4 pb-4 pt-2">
            <NavLink
              to="/it"
              className={(p) => `block py-2 ${navLinkClassIt(p)}`}
              onClick={closeMobileMenu}
            >
              {t("header.navItSolutions")}
            </NavLink>
            <a
              href="https://vandro.ai/"
              target="_blank"
              rel="noopener noreferrer"
              className="block py-2 text-sm font-medium text-primary-700 transition-colors hover:text-accent-500"
              onClick={closeMobileMenu}
            >
              {t("header.navVandro")}
            </a>
            <NavLink
              to="/vandro-automation"
              className={(p) => `block py-2 ${navLinkClassAutomation(p)}`}
              onClick={closeMobileMenu}
            >
              {t("header.navAutomation")}
            </NavLink>
            {/* Mobile Language Selector */}
            <div className="pt-2 border-t">
              <button
                onClick={() => changeLanguage('ko')}
                className="flex items-center w-full text-left py-2 text-primary-700 hover:text-accent-500 font-medium"
              >
                <span className="text-xl mr-2">🇰🇷</span>
                {t("header.language.ko")}
              </button>
              <button
                onClick={() => changeLanguage('en')}
                className="flex items-center w-full text-left py-2 text-primary-700 hover:text-accent-500 font-medium"
              >
                <span className="text-xl mr-2">🇺🇸</span>
                {t("header.language.en")}
              </button>
              <button
                onClick={() => changeLanguage('ja')}
                className="flex items-center w-full text-left py-2 text-primary-700 hover:text-accent-500 font-medium"
              >
                <span className="text-xl mr-2">🇯🇵</span>
                {t("header.language.ja")}
              </button>
              <button
                onClick={() => changeLanguage('es')}
                className="flex items-center w-full text-left py-2 text-primary-700 hover:text-accent-500 font-medium"
              >
                <span className="text-xl mr-2">🇪🇸</span>
                {t("header.language.es")}
              </button>
              <button
                onClick={() => changeLanguage('pt')}
                className="flex items-center w-full text-left py-2 text-primary-700 hover:text-accent-500 font-medium"
              >
                <span className="text-xl mr-2">🇧🇷</span>
                {t("header.language.pt")}
              </button>
              <button
                onClick={() => changeLanguage('fr')}
                className="flex items-center w-full text-left py-2 text-primary-700 hover:text-accent-500 font-medium"
              >
                <span className="text-xl mr-2">🇫🇷</span>
                {t("header.language.fr")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
